import type { CreatorInfo } from '@ps/types/creator';

/**
 * sessionStorage handoff for the login modal's brand chrome. A surface that
 * navigates to the app-level `/login` (a bio account pill, the subscribe gate, a
 * DM login gate, a video login prompt) writes the creator's {@link CreatorInfo}
 * here just before navigating, so the intercepted `@login/(.)login` can render
 * "signing in to <creator>" (avatar + name + the creator's own privacy policy +
 * any customized member copy) WITHOUT a server fetch. This module is the single
 * owner of the key and shape — every writer and the one reader go through it, so
 * there is no magic string to keep in lock-step. Standalone `/login` cold-loads
 * read nothing and render the bare form.
 *
 * `CreatorInfo` is a plain, timestamp-free projection, so it is safe to persist
 * and to cross the Server→Client boundary (unlike a raw Firebase profile). The
 * login reader adapts it to the `PhoneAuthForm` brand shape.
 *
 * sessionStorage (not localStorage) so a stale handoff can't bleed across tabs.
 */
const LOGIN_BRAND_HANDOFF_KEY = 'login:brand';

/** Persist the creator brand for the next `/login` open. No-op off the browser. */
export function writeLoginBrandHandoff(creator: CreatorInfo): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.sessionStorage.setItem(
      LOGIN_BRAND_HANDOFF_KEY,
      JSON.stringify(creator),
    );
  } catch {
    // Quota / private-mode — the modal just renders without brand chrome.
  }
}

/**
 * Read the brand handoff WITHOUT clearing it. Pure + repeatable — safe to call
 * from a `useState` lazy initializer, which React may invoke more than once for
 * a render it ends up discarding (interrupted/concurrent rendering). Pair with
 * {@link clearLoginBrandHandoff} in a `useEffect` for the one-shot clear — an
 * effect only runs for a render that actually commits, so it can't be the thing
 * that silently discards the handoff before the real open sees it.
 */
export function peekLoginBrandHandoff(): CreatorInfo | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const raw = window.sessionStorage.getItem(LOGIN_BRAND_HANDOFF_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as CreatorInfo;
    if (parsed && (parsed.id || parsed.name || parsed.avatar)) {
      return parsed;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** Clear the brand handoff so it can't bleed into the next `/login` open. */
export function clearLoginBrandHandoff(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(LOGIN_BRAND_HANDOFF_KEY);
}
